/* eslint-disable complexity */
import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { isArray, isEqual } from 'lodash';

import { Accordion } from 'carbon-react/lib/components/accordion';
import { MultiSelect, Option, Select } from 'carbon-react/lib/components/select';
import Textbox from 'carbon-react/lib/components/textbox';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import {
  CARD_TYPES,
  ROUNDING_OPTIONS,
  SCALING_OPTIONS,
  VALUE_TREATMENT,
  WIDGET_TYPES,
  VALUE_DISPLAY_OPTIONS,
  AxisOptions,
  GROUPING_OPTIONS,
  GROUPING_LEVEL
} from '../../../types';
import { AvailableChartTypes, AvailableChartTypesSizes } from '../available-chart-types/available-chart-types';
import { ClassifierLevel } from '../../../constants';
import { useReportBuilderContext } from '../../../report-builder.context';
import { getPossibleSecondaryChartTypes, widgetHasTwoAxes } from '../../../utils-data';
import { usePredictionsInfoContext } from '../../../../forecast/detail/context/predictions-info-context';
import { useI18nConstants } from '../../../use-i18n-constants';

import styles from './settings-panel.scss';

const excludedClassifiers = ['row-formula', 'template-formula', 'freestyle-driver', 'formula-driver'];

interface Props {
  axis: string;
  isSecondary?: boolean;
}
export const SettingsAxisPanel = ({ axis, isSecondary }: Props) => {
  const intl = useIntl();
  const {
    setPrimaryAxisOptions,
    setSecondaryAxisOptions,
    currentCard,
    getSelectedFinFields,
    setFinFieldOptionsAllFields
  } = useReportBuilderContext();
  const { isConsolidation } = usePredictionsInfoContext();
  const {
    roundingOptions,
    scalingOptions,
    valueTreatmentOptions,
    unitOptions,
    groupingOptions,
    driversGroupingOptions
  } = useI18nConstants();
  const widgetType = currentCard?.[CARD_TYPES.WIDGET]?.widgetType as WIDGET_TYPES;
  const isCircularChartType =
    widgetType === WIDGET_TYPES.PIE || widgetType === WIDGET_TYPES.DONUT || widgetType === WIDGET_TYPES.DATA_CARD;
  const hasComparisons = (currentCard?.[CARD_TYPES.WIDGET]?.selectedComparisons || []).length > 0;

  const initAxisOptions = useMemo(
    () =>
      currentCard &&
      (isSecondary
        ? currentCard[CARD_TYPES.WIDGET]?.secondaryAxisOptions
        : currentCard[CARD_TYPES.WIDGET]?.primaryAxisOptions),
    [currentCard, isSecondary]
  );
  const [scaling, setScaling] = useState(initAxisOptions?.scaling || SCALING_OPTIONS.AUTO);
  const [min, setMin] = useState<number | string | undefined>(initAxisOptions?.min);
  const [max, setMax] = useState<number | string | undefined>(initAxisOptions?.max);
  const [rounding, setRounding] = useState<ROUNDING_OPTIONS>(initAxisOptions?.rounding || ROUNDING_OPTIONS.INTEGER);
  const [unit, setUnit] = useState<VALUE_DISPLAY_OPTIONS | string>(initAxisOptions?.unit || '');
  const [valueTreatment, setValueTreatment] = useState<VALUE_TREATMENT>(
    initAxisOptions?.valueTreatment || VALUE_TREATMENT.SUM
  );
  const [secondaryChartType, setSecondaryChartType] = useState<WIDGET_TYPES | undefined>(
    (initAxisOptions?.chartType || widgetType) as WIDGET_TYPES
  );
  const [grouping, setGrouping] = useState<GROUPING_OPTIONS[]>([]);
  const [singleGrouping, setSingleGrouping] = useState<GROUPING_OPTIONS>(grouping[0] || groupingOptions[0].value);
  const updateTimer = useRef<number | null>(null);

  // selectedFinFields that can have grouping
  const selectedFinFieldsWithGrouping = useMemo(
    () =>
      getSelectedFinFields().filter(
        f => !f.isSecondary === !isSecondary && !excludedClassifiers.includes(f.classifier)
      ),
    [getSelectedFinFields, isSecondary]
  );

  const handleScalingChange = (event: ChangeEvent<HTMLInputElement>) => {
    setScaling(event.target.value as SCALING_OPTIONS);
    if (event.target.value === SCALING_OPTIONS.AUTO) {
      setMin(undefined);
      setMax(undefined);
    }
  };

  const handleRoundingChange = (event: ChangeEvent<HTMLInputElement>) => {
    setRounding(event.target.value as ROUNDING_OPTIONS);
  };

  const handleUnitChange = (event: ChangeEvent<HTMLInputElement>) => {
    setUnit(event.target.value as VALUE_DISPLAY_OPTIONS);
  };

  const handleValueTreatmentChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValueTreatment(event.target.value as VALUE_TREATMENT);
  };

  const handleMinChange = (value: string) => {
    setMin(value);
  };

  const handleMaxChange = (value: string) => {
    setMax(value);
  };

  const [subGroupingOptions, excludeDriverGroups] = useMemo(() => {
    let subGO = [...groupingOptions];
    if (isConsolidation || hasComparisons) {
      // if we have comparisons between datasets, we can't compare assumptions, as they are specific to each dataset
      // consolidations also don't have assumptions
      subGO = subGO.filter(e => e.value !== GROUPING_OPTIONS.ASSUMPTIONS);
    }
    let lowestClassifier = 10;
    let allDriverGroups = true;
    (currentCard?.[CARD_TYPES.WIDGET]?.finFieldsOptions || []).forEach(fieldOptions => {
      const field = selectedFinFieldsWithGrouping.find(f => f.ref === fieldOptions.ref);
      if (field) {
        if (allDriverGroups && field.classifier !== 'driver-group') {
          allDriverGroups = false;
        }
        if (lowestClassifier > ClassifierLevel[field.classifier] && field.classifier !== 'driver-group') {
          lowestClassifier = ClassifierLevel[field.classifier];
        }
      }
    });
    // if all selected fields are driver groups, we return the options for driver groups
    if (allDriverGroups) {
      return [driversGroupingOptions, false];
    }
    // the list of option should match the selected field with the least options so that we can set it for all selected fields
    switch (lowestClassifier) {
      case GROUPING_LEVEL.CATEGORIES:
        return [subGO, true];
      case GROUPING_LEVEL.GROUPS:
        return [subGO.filter(e => e.value !== GROUPING_OPTIONS.CATEGORIES), true];
      case GROUPING_LEVEL.ACCOUNTS:
        return [
          subGO.filter(e => e.value !== GROUPING_OPTIONS.GROUPS && e.value !== GROUPING_OPTIONS.CATEGORIES),
          true
        ];
      default:
        return [[], true];
    }
  }, [
    groupingOptions,
    isConsolidation,
    hasComparisons,
    currentCard,
    selectedFinFieldsWithGrouping,
    driversGroupingOptions
  ]);

  useEffect(() => {
    // rules for default grouping for axis:
    // if all fields have the same grouping, that value is the default grouping for the axis
    // otherwise, default grouping is empty
    // that excludes excludedClassifiers and driver-groups if they're mixed with other fields
    let defaultGrouping = '';
    (currentCard?.[CARD_TYPES.WIDGET]?.finFieldsOptions || []).some(opt => {
      const field = selectedFinFieldsWithGrouping.find(f => f.ref === opt.ref);
      if (!field || (excludeDriverGroups && field.classifier === 'driver-group')) {
        return false;
      }
      const fieldGrouping = (opt.options?.grouping || []).sort((a, b) => a.localeCompare(b)).join('#');
      if (!defaultGrouping) {
        defaultGrouping = fieldGrouping;
        return false;
      } else if (defaultGrouping !== fieldGrouping) {
        defaultGrouping = '';
        return true;
      } else {
        return false;
      }
    });
    if (defaultGrouping) {
      setGrouping(defaultGrouping.split('#') as GROUPING_OPTIONS[]);
      setSingleGrouping(defaultGrouping.split('#')[0] as GROUPING_OPTIONS);
    } else {
      setGrouping([]);
    }
  }, [currentCard, selectedFinFieldsWithGrouping, excludeDriverGroups]);

  useEffect(() => {
    updateTimer.current && clearTimeout(updateTimer.current);
    updateTimer.current = (setTimeout(() => {
      const newSettings: AxisOptions = {
        scaling,
        min: min === '' ? undefined : +(min || 0),
        max: max === '' ? undefined : +(max || 0),
        rounding,
        valueTreatment
      };
      if (unit) {
        newSettings.unit = unit as VALUE_DISPLAY_OPTIONS;
      }
      if (isSecondary && secondaryChartType) {
        newSettings.chartType = secondaryChartType as WIDGET_TYPES;
      }

      // Don't send request when min >= max or both min and max are equal to 0
      const minMaxError =
        newSettings.scaling === SCALING_OPTIONS.MIN_MAX &&
        typeof newSettings.min === 'number' &&
        typeof newSettings.max === 'number' &&
        (newSettings.min >= newSettings.max || (newSettings.min === 0 && newSettings.max === 0));

      if (!isEqual(newSettings, initAxisOptions) && !minMaxError) {
        isSecondary ? setSecondaryAxisOptions(newSettings) : setPrimaryAxisOptions(newSettings);
      }
      // eslint-disable-next-line no-magic-numbers
    }, 1000) as unknown) as number;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scaling, min, max, rounding, valueTreatment, isSecondary, unit, secondaryChartType, initAxisOptions]);

  const possibleChartTypes = useMemo(() => getPossibleSecondaryChartTypes(widgetType), [widgetType]);

  useEffect(() => {
    if (widgetType) {
      if (!possibleChartTypes.includes(secondaryChartType as WIDGET_TYPES)) {
        setSecondaryChartType(possibleChartTypes[0]);
      }
    }
  }, [widgetType, possibleChartTypes, secondaryChartType]);

  const customUnitOptions = useMemo(
    () =>
      [WIDGET_TYPES.PIE, WIDGET_TYPES.DONUT].includes(widgetType)
        ? unitOptions.filter(o => o.value !== VALUE_DISPLAY_OPTIONS.PERCENTAGE_RAW)
        : unitOptions,
    [unitOptions, widgetType]
  );

  const onSelectSecondaryAxisChartType = (e: React.MouseEvent, item: WIDGET_TYPES) => {
    setSecondaryChartType(item);
  };

  const handleSingleGroupingChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as GROUPING_OPTIONS;
    if (singleGrouping !== value) {
      setSingleGrouping(value);
      handleGroupingChange([value]);
    }
  };

  const handleGroupingChange = (value: GROUPING_OPTIONS[] | GROUPING_OPTIONS) => {
    let newGrouping: GROUPING_OPTIONS[] = [];
    if (isArray(value)) {
      newGrouping = value;
    } else {
      newGrouping = [value];
    }
    if (grouping.join('#') !== newGrouping.join('#')) {
      setGrouping(newGrouping);
      // we set new field options for all fields except excludedClassifiers and driver-group if they're mixed with other fields
      setFinFieldOptionsAllFields(
        { grouping: newGrouping },
        isSecondary,
        excludeDriverGroups ? [...excludedClassifiers, 'driver-group'] : excludedClassifiers
      );
    }
  };

  if (
    widgetType === WIDGET_TYPES.TABLE &&
    (selectedFinFieldsWithGrouping.length <= 1 || subGroupingOptions.length <= 1)
  ) {
    return null;
  }

  return (
    <div className={clsx(styles.expandPanelContainerCarbon)}>
      <Accordion
        borders="full"
        title={
          <FormattedMessage
            id="reporting.default_axis_setings"
            defaultMessage="Default {axis} settings"
            values={{ axis }}
          />
        }
        headerSpacing={{ p: 0 }}
      >
        {currentCard && widgetType !== WIDGET_TYPES.TABLE && (
          <Select
            id="unit"
            autoComplete="off"
            onChange={handleUnitChange}
            value={unit}
            size="small"
            label={intl.formatMessage({ id: 'common.unit', defaultMessage: 'Unit' })}
            mb={1}
            placeholder={intl.formatMessage({ id: 'common.predict.please_select', defaultMessage: 'Please select...' })}
          >
            {customUnitOptions.map(option => (
              <Option key={option.value} value={option.value} text={option.name} />
            ))}
          </Select>
        )}
        {currentCard && widgetType !== WIDGET_TYPES.TABLE && (
          <Select
            id="rounding"
            autoComplete="off"
            onChange={handleRoundingChange}
            value={rounding}
            size="small"
            label={intl.formatMessage({ id: 'reporting.rounding_override', defaultMessage: 'Rounding override' })}
            mb={1}
            placeholder={intl.formatMessage({ id: 'common.predict.please_select', defaultMessage: 'Please select...' })}
          >
            {roundingOptions.map(option => (
              <Option key={option.value} value={option.value} text={option.name} />
            ))}
          </Select>
        )}
        {currentCard && [WIDGET_TYPES.PIE, WIDGET_TYPES.DONUT, WIDGET_TYPES.DATA_CARD].includes(widgetType) && (
          <Select
            id="value-treatment"
            autoComplete="off"
            onChange={handleValueTreatmentChange}
            value={valueTreatment}
            size="small"
            label={intl.formatMessage({ id: 'reporting.values_treatment', defaultMessage: 'Values treatment' })}
            mb={1}
            placeholder={intl.formatMessage({ id: 'common.predict.please_select', defaultMessage: 'Please select...' })}
          >
            {valueTreatmentOptions.map(option => (
              <Option key={option.value} value={option.value} text={option.name} />
            ))}
          </Select>
        )}
        {currentCard && widgetHasTwoAxes(widgetType) && (
          <div>
            <Select
              id="scaling"
              autoComplete="off"
              onChange={handleScalingChange}
              value={scaling}
              size="small"
              label={intl.formatMessage({ id: 'reporting.scaling', defaultMessage: 'Scaling' })}
              mb={1}
              placeholder={intl.formatMessage({
                id: 'common.predict.please_select',
                defaultMessage: 'Please select...'
              })}
            >
              {scalingOptions.map(option => (
                <Option key={option.value} value={option.value} text={option.name} />
              ))}
            </Select>
            {scaling === SCALING_OPTIONS.MIN_MAX && (
              <div className={clsx(styles.rangeContainer)}>
                <Textbox
                  value={`${min}`}
                  onChange={e => {
                    if (!/^-?[0-9]*$/.test(e.target.value)) {return;}
                    handleMinChange(e.target.value);
                  }}
                  size="small"
                />
                <Textbox
                  value={`${max}`}
                  onChange={e => {
                    if (!/^-?[0-9]*$/.test(e.target.value)) {return;}
                    handleMaxChange(e.target.value);
                  }}
                  size="small"
                  marginTop={0}
                />
              </div>
            )}
          </div>
        )}
        {selectedFinFieldsWithGrouping.length > 1 &&
          subGroupingOptions.length > 1 &&
          (isCircularChartType ? (
            <Select
              id="grouping"
              value={singleGrouping}
              listMaxHeight={200}
              label={intl.formatMessage({
                id: 'reporting.data_includes_override',
                defaultMessage: 'Data includes override'
              })}
              onChange={handleSingleGroupingChange}
              mb={1}
              size="small"
              placeholder={intl.formatMessage({
                id: 'common.predict.please_select',
                defaultMessage: 'Please select...'
              })}
            >
              {subGroupingOptions.map(option => (
                <Option key={option.value} value={option.value} text={option.name} />
              ))}
            </Select>
          ) : (
            <MultiSelect
              id="grouping"
              value={grouping}
              label={intl.formatMessage({
                id: 'reporting.data_includes_override',
                defaultMessage: 'Data includes override'
              })}
              onChange={e => handleGroupingChange(e.target.value as GROUPING_OPTIONS | GROUPING_OPTIONS[])}
              mb={1}
              size="small"
              placeholder={intl.formatMessage({
                id: 'common.predict.please_select',
                defaultMessage: 'Please select...'
              })}
            >
              {subGroupingOptions.map(option => (
                <Option key={option.value} value={option.value} text={option.name} />
              ))}
            </MultiSelect>
          ))}
        {isSecondary && possibleChartTypes.length > 0 && (
          <AvailableChartTypes
            currentWidgetType={secondaryChartType}
            onClick={onSelectSecondaryAxisChartType}
            size={AvailableChartTypesSizes.MEDIUM}
            widgetTypes={possibleChartTypes}
            className={styles.mt12}
          />
        )}
      </Accordion>
    </div>
  );
};
